import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchPurchaseOrders,
  fetchPoFiltersData,
  fetchPurchaseOrderDetail,
  setPoFilters,
  resetPoFilters,
  setPoPage,
  toggleSelectedId,
  clearSelectedIds,
  deletePurchaseOrder,
  submitPurchaseOrder,
  unsubmitPurchaseOrder,
  cancelPurchaseOrder,
  recreatePurchaseOrder,
  splitPurchaseOrder,
  mergePurchaseOrders,
  rejectAtrPurchaseOrder,
  syncJobCards,
  syncTechnicians,
  createTbaJobcards,
  sendSupplier,
  bulkSetType,
  bulkSetQuotationVat,
  bulkSetItemOrder,
  bulkMarkNonVat,
  bulkSubmitPayment,
  receiveStock,
  undoReceive,
  bulkSendToMetrofile,
  bulkExportToMetrofile,
  fulfillPurchaseOrder,
  downloadSageExport,
  fetchPoItems,
  addPoItem,
  updatePoItem,
  deletePoItem,
  loadAtrItems,
  PoListItem,
  PoMinimalStore,
  PoDetail,
} from './poSlice';
import { AppDispatch, RootState } from '../../app/store';
import toast from 'react-hot-toast';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatUtils';
import { getPoRowColor } from '../../utils/statusUtils';
import ViewDocumentsModal from './ViewDocumentsModal';
import AddDocumentModal from './AddDocumentModal';
import { deletePoDocumentApi, importQuotationLineItemsApi, downloadExcelTemplateApi } from '../../services/api';

const getPoStatusDisplay = (po: PoListItem): { text: string; bgColor: string; textColor: string } => {
  const statusId = po.status_id;
  const submitted = typeof po.submitted === 'number' ? po.submitted !== 0 : po.submitted;

  if (statusId === 2) return { text: 'Approved', bgColor: 'bg-white/20', textColor: 'text-white' };
  if (statusId === 3) return { text: 'Rejected', bgColor: 'bg-white/20', textColor: 'text-white' };
  if (statusId === 4) return { text: 'Cancelled', bgColor: 'bg-white/20', textColor: 'text-white' };
  if (!!po.reference_number && !po.order_number) return { text: 'TBA', bgColor: 'bg-white/20', textColor: 'text-white' };
  if (submitted) return { text: 'Submitted', bgColor: 'bg-white/20', textColor: 'text-white' };
  return { text: 'Pending', bgColor: 'bg-white/20', textColor: 'text-white' };
};

const STORE_PREVIEW_LIMIT = 3;

const StorePopover: React.FC<{ stores: PoMinimalStore[]; className?: string }> = ({ stores, className }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const count = stores?.length || 0;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (count === 0) return <span className={className}>N/A</span>;
  if (count === 1) return <span className={className}>{stores[0].name}</span>;

  const preview = stores.slice(0, STORE_PREVIEW_LIMIT).map((s) => s.name).join(', ');
  const remaining = count - STORE_PREVIEW_LIMIT;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`cursor-pointer hover:underline ${className || ''}`}
      >
        <strong>({count} stores)</strong> {preview}
        {!!(remaining > 0) && <span> +{remaining} more</span>}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 min-w-[200px] max-w-[300px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg p-3 text-left">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 pb-2 border-b border-slate-200 dark:border-slate-700">
            {count} Store{count !== 1 ? 's' : ''}
          </div>
          <ul className="space-y-1 max-h-[200px] overflow-auto">
            {stores.map((s, i) => (
              <li key={s.id || i} className="text-sm text-slate-700 dark:text-slate-200 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                {s.name}
                {!!s.storenumber && <span className="text-xs text-slate-400">#{s.storenumber}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const StoreList: React.FC<{ stores: PoMinimalStore[] }> = ({ stores }) => {
  return <StorePopover stores={stores} className="font-medium text-yellow-300" />;
};

// SVG icons matching FontAwesome icons from Livewire
const ClockIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
  </svg>
);

const PaperPlaneIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
  </svg>
);

const CheckDoubleIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" opacity="0.5" transform="translate(2, 2)" />
  </svg>
);

const TimesIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const BanIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
  </svg>
);

// Cache for PO details to avoid re-fetching
const sharedPoDetailsCache = new Map<number, any[]>();
const poLoadingStates = new Set<number>();

interface PoProcessedItem {
  id: string;
  type: string;
  title: string;
  subtitle?: string;
  details?: string;
  descriptions?: string[];
  badgeColor: string;
}

const PoItemsPreview: React.FC<{ poId: number }> = ({ poId }) => {
  const dispatch: AppDispatch = useDispatch();
  const [items, setItems] = useState<PoProcessedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasLoaded) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [hasLoaded]);

  useEffect(() => {
    if (isVisible && !hasLoaded && !loading) {
      const cachedItems = sharedPoDetailsCache.get(poId);
      if (cachedItems) {
        setItems(cachedItems);
        setExpanded(true);
        setShowAll(false);
        setHasLoaded(true);
        return;
      }
      if (poLoadingStates.has(poId)) return;

      setLoading(true);
      poLoadingStates.add(poId);

      dispatch(fetchPurchaseOrderDetail(poId))
        .unwrap()
        .then((response: any) => {
          const po: PoDetail = response.data || response;
          const processedItems: PoProcessedItem[] = [];

        if (po.equipment_items && po.equipment_items.length > 0) {
          po.equipment_items.forEach((item: any, index: number) => {
            const catSub = [item.category, item.subcategory].filter(Boolean).join(' - ');
            processedItems.push({
              id: `po-eq-${index}`,
              type: 'Equipment',
              title: item.description || 'Equipment Item',
              subtitle: catSub ? `Category: ${catSub}` : '',
              details: item.serial_number ? `S/N: ${item.serial_number}` : '',
              descriptions: item.repair_notes ? [item.repair_notes] : undefined,
              badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200',
            });
          });
        }

        if (po.asset_description_pairs && po.asset_description_pairs.length > 0) {
          po.asset_description_pairs.forEach((pair: any, index: number) => {
            processedItems.push({
              id: `po-asset-${index}`,
              type: 'Asset',
              title: `Asset: ${pair.asset}`,
              descriptions: pair.descriptions,
              badgeColor: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200',
            });
          });
        }

        if (processedItems.length === 0) {
          if (po.equipmentRequests && po.equipmentRequests.length > 0) {
            po.equipmentRequests.forEach((item: any, index: number) => {
              processedItems.push({
                id: `po-eq-legacy-${index}`,
                type: 'Equipment',
                title: item.equipment?.description || 'Equipment Item',
                subtitle: item.equipment?.serial_number ? `S/N: ${item.equipment.serial_number}` : '',
                details: item.notes || '',
                badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200',
              });
            });
          }
          if (po.assets && po.assets.length > 0) {
            po.assets.forEach((item: any, index: number) => {
              const descriptions = item.descriptions?.map((desc: any) => desc.description) || [];
              processedItems.push({
                id: `po-asset-legacy-${index}`,
                type: 'Asset',
                title: `Asset: ${item.asset}`,
                descriptions,
                badgeColor: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200',
              });
            });
          }
          if (po.repairs && po.repairs.length > 0 && (!po.assets || po.assets.length === 0)) {
            po.repairs.forEach((repair: any, index: number) => {
              processedItems.push({
                id: `po-repair-${index}`,
                type: 'Legacy',
                title: `Item ${index + 1}`,
                descriptions: [repair.description],
                badgeColor: 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200',
              });
            });
          }
        }

        sharedPoDetailsCache.set(poId, processedItems);
        setItems(processedItems);
        setExpanded(true);
        setShowAll(false);
        setHasLoaded(true);
        setLoading(false);
        poLoadingStates.delete(poId);
      })
      .catch(() => {
        setLoading(false);
        setHasLoaded(true);
        poLoadingStates.delete(poId);
      });
    }
  }, [isVisible, hasLoaded, loading, poId, dispatch]);

  return (
    <div className="mt-2" ref={containerRef}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center text-xs font-medium text-white/70 hover:text-white transition-colors"
      >
        <svg
          className={`w-3 h-3 mr-1 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Items to Repair / Request
        {!!loading && <span className="text-xs text-white/50 ml-1">(Loading...)</span>}
        {items.length > 0 && !loading && <span className="ml-1">({items.length})</span>}
      </button>

      {!!expanded && !!hasLoaded && !loading && (
        <div className="mt-1 space-y-1">
          {items.length > 0 ? (
            <>
              {(showAll ? items : items.slice(0, 2)).map((item) => (
                <div key={item.id} className="p-1.5 rounded text-xs bg-white/10">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-white/90">{item.title}</p>
                      {!!item.subtitle && <p className="text-white/60 mt-0.5">{item.subtitle}</p>}
                      {!!item.details && <p className="text-white/60 mt-0.5">{item.details}</p>}
                      {!!item.descriptions && item.descriptions.length > 0 && (
                        <div className="mt-0.5">
                          <p className="text-white/60"><span className="font-medium">Description:</span></p>
                          <ul className="list-disc list-inside ml-1 text-white/60">
                            {item.descriptions.map((desc, dIndex) =>
                              !!desc && <li key={dIndex}>{desc}</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                    <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${item.badgeColor}`}>
                      {item.type.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
              {items.length > 2 && (
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-xs font-medium text-yellow-300 hover:text-yellow-200 transition-colors"
                >
                  {showAll ? 'Show less' : `Show ${items.length - 2} more`}
                </button>
              )}
            </>
          ) : (
            <p className="text-white/50 italic">No items listed for repair or purchase.</p>
          )}
        </div>
      )}
    </div>
  );
};

const PO_FILTERS_STORAGE_KEY = 'po_filters';

const PurchaseOrderListPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const {
    list,
    loading,
    actionLoading,
    error,
    statusCounts,
    filtersData,
    filters,
    currentPage,
    lastPage,
    total,
    selectedIds,
  } = useSelector((state: RootState) => state.purchaseOrders);
  const { user } = useSelector((state: RootState) => state.auth);

  // Local state for searchable dropdowns
  const [storeSearchTerm, setStoreSearchTerm] = useState('');
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false);
  const [storeDropdownRef, setStoreDropdownRef] = useState<HTMLDivElement | null>(null);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [isSupplierDropdownOpen, setIsSupplierDropdownOpen] = useState(false);
  const [supplierDropdownRef, setSupplierDropdownRef] = useState<HTMLDivElement | null>(null);

  // View Documents Modal state
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [selectedPoForDocuments, setSelectedPoForDocuments] = useState<PoDetail | null>(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  // Add Document Modal state
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [selectedPoForAddDocument, setSelectedPoForAddDocument] = useState<PoDetail | null>(null);

  // Advanced filters collapsible state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Admin actions dropdown state
  const [isAdminActionsOpen, setIsAdminActionsOpen] = useState(false);
  const [adminActionsRef, setAdminActionsRef] = useState<HTMLDivElement | null>(null);

  // Actions dropdown state (must be declared before useEffect that references them)
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);

  // Close admin actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (adminActionsRef && !adminActionsRef.contains(e.target as Node)) {
        setIsAdminActionsOpen(false);
      }
    };
    if (isAdminActionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAdminActionsOpen, adminActionsRef]);

  // Close actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(e.target as Node)) {
        setShowActionsDropdown(false);
      }
    };
    if (showActionsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionsDropdown]);

  // Load saved filters from localStorage on mount (before first API call)
  const [hasLoadedSavedFilters, setHasLoadedSavedFilters] = useState(false);
  useEffect(() => {
    try {
      const saved = localStorage.getItem(PO_FILTERS_STORAGE_KEY);
      if (saved) {
        const savedFilters = JSON.parse(saved);
        savedFilters.per_page = 20;
        dispatch(setPoFilters(savedFilters));
      }
    } catch (e) {
      console.error('Failed to load saved PO filters:', e);
    }
    setHasLoadedSavedFilters(true);
  }, [dispatch]);

  // Load filters data on mount
  useEffect(() => {
    dispatch(fetchPoFiltersData());
  }, [dispatch]);

  // Load PO list whenever filters change (only after saved filters are loaded)
  useEffect(() => {
    if (!hasLoadedSavedFilters) return;
    dispatch(fetchPurchaseOrders(filters));
  }, [dispatch, filters, hasLoadedSavedFilters]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (!hasLoadedSavedFilters) return;
    try {
      localStorage.setItem(PO_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
      console.error('Failed to save PO filters:', e);
    }
  }, [filters, hasLoadedSavedFilters]);

  // Live search with debounce
  const [searchInput, setSearchInput] = useState(filters.search || '');
  useEffect(() => {
    if (!hasLoadedSavedFilters) return;
    const timer = setTimeout(() => {
      if (searchInput !== (filters.search || '')) {
        dispatch(setPoFilters({ search: searchInput || undefined }));
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, hasLoadedSavedFilters, dispatch, filters.search]);

  // Sync searchInput when filters change externally (e.g. reset)
  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (storeDropdownRef && !storeDropdownRef.contains(e.target as Node)) {
        setIsStoreDropdownOpen(false);
      }
      if (supplierDropdownRef && !supplierDropdownRef.contains(e.target as Node)) {
        setIsSupplierDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [storeDropdownRef, supplierDropdownRef]);

  const handleFilterChange = (key: string, value: any) => {
    dispatch(setPoFilters({ [key]: value }));
  };

  const handlePageChange = (page: number) => {
    dispatch(setPoPage(page));
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setStoreSearchTerm('');
    setSupplierSearchTerm('');
    dispatch(resetPoFilters());
  };

  // Store multi-select helpers
  const selectedStoreIds = filters.stores || [];
  const handleStoreToggle = (storeId: number) => {
    const newStores = selectedStoreIds.includes(storeId)
      ? selectedStoreIds.filter(id => id !== storeId)
      : [...selectedStoreIds, storeId];
    dispatch(setPoFilters({ stores: newStores.length > 0 ? newStores : undefined }));
  };
  const filteredStores = (filtersData?.stores || []).filter(s => {
    const term = storeSearchTerm.toLowerCase();
    return s.name.toLowerCase().includes(term) || (s.storenumber || '').toLowerCase().includes(term);
  });

  // Supplier single-select helpers
  const selectedSupplier = (filtersData?.suppliers || []).find(s => s.id === filters.supplier_id);
  const filteredSuppliers = (filtersData?.suppliers || []).filter(s => {
    const term = supplierSearchTerm.toLowerCase();
    return s.name.toLowerCase().includes(term);
  });

  const handleDelete = async (id: number) => {
    requestConfirm(
      'Delete Purchase Order',
      'Are you sure you want to delete this purchase order? This cannot be undone.',
      () => doDelete(id),
      'Delete',
      'bg-red-600'
    );
  };

  const doDelete = async (id: number) => {
    try {
      await dispatch(deletePurchaseOrder(id)).unwrap();
      toast.success('Purchase order deleted');
    } catch (err: any) {
      toast.error(err || 'Failed to delete');
    }
  };

  const handleViewDocuments = async (poId: number) => {
    setDocumentsLoading(true);
    setIsDocumentsModalOpen(true);
    try {
      const poDetail = await dispatch(fetchPurchaseOrderDetail(poId)).unwrap();
      setSelectedPoForDocuments(poDetail.data || poDetail);
    } catch (err: any) {
      toast.error(err || 'Failed to load documents');
      setIsDocumentsModalOpen(false);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const getDocumentCount = (po: PoListItem): number => {
    const quotationCount = po.quotation ? 1 : 0;
    const deliverynotesCount = po.deliverynotes_count || 0;
    const jobcardsCount = po.jobcards_count || 0;
    const invoicesCount = po.invoices_count || 0;
    const ticketsCount = po.tickets_count || 0;
    const othersCount = po.others_count || 0;
    return quotationCount + deliverynotesCount + jobcardsCount + invoicesCount + ticketsCount + othersCount;
  };

  const handleAddDocument = async (poId: number) => {
    setDocumentsLoading(true);
    setIsAddDocumentModalOpen(true);
    try {
      const poDetail = await dispatch(fetchPurchaseOrderDetail(poId)).unwrap();
      setSelectedPoForAddDocument(poDetail.data || poDetail);
    } catch (err: any) {
      toast.error(err || 'Failed to load PO details');
      setIsAddDocumentModalOpen(false);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleDocumentUploaded = (updatedPo: PoDetail) => {
    setSelectedPoForAddDocument(updatedPo);
    setIsAddDocumentModalOpen(false);
    // Refresh the list to show updated document counts
    dispatch(fetchPurchaseOrders(filters));
  };

  const handleDeleteDocument = async (type: string, documentId: number) => {
    if (!selectedPoForDocuments?.id) return;
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await deletePoDocumentApi(selectedPoForDocuments.id, type, documentId);
      toast.success('Document deleted successfully');
      // Refresh the PO detail to show updated documents
      await dispatch(fetchPurchaseOrderDetail(selectedPoForDocuments.id)).unwrap();
      // Refresh the list to show updated document counts
      dispatch(fetchPurchaseOrders(filters));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete document');
    }
  };

  const [showRejectAtrModal, setShowRejectAtrModal] = useState(false);
  const [rejectAtrComment, setRejectAtrComment] = useState('');
  const [showBulkCancelModal, setShowBulkCancelModal] = useState(false);
  const [bulkCancelReason, setBulkCancelReason] = useState('');
  const [bulkCancelRecreate, setBulkCancelRecreate] = useState<'0' | '1'>('0');
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mainOrderId, setMainOrderId] = useState<number | null>(null);
  const [showTbaModal, setShowTbaModal] = useState(false);
  const [tbaApprover, setTbaApprover] = useState<number | null>(null);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveDate, setReceiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [showMetrofileModal, setShowMetrofileModal] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [showSageExportModal, setShowSageExportModal] = useState(false);
  const [sageExportParams, setSageExportParams] = useState({ date_from: '', date_to: '', order_numbers: '' });

  // Generic confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmLabel: string;
    confirmColor: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', confirmLabel: 'Confirm', confirmColor: 'bg-blue-600', onConfirm: () => {} });

  const requestConfirm = (title: string, message: string, onConfirm: () => void, confirmLabel = 'Confirm', confirmColor = 'bg-blue-600') => {
    setConfirmModal({ open: true, title, message, confirmLabel, confirmColor, onConfirm });
  };

  const handleConfirmedAction = async () => {
    setConfirmModal(prev => ({ ...prev, open: false }));
    await confirmModal.onConfirm();
  };
  const [showManageItemsModal, setShowManageItemsModal] = useState(false);
  const [manageItemsPoId, setManageItemsPoId] = useState<number | null>(null);
  const [manageItemsList, setManageItemsList] = useState<any[]>([]);
  const [manageItemsLoading, setManageItemsLoading] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', description: '', quantity: '', price: '', item_code: '', vat: 'inclusive', non_vat: false });
  const [keepExistingItems, setKeepExistingItems] = useState(false);
  const [atrLoading, setAtrLoading] = useState(false);
  const [excelImportLoading, setExcelImportLoading] = useState(false);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [manageItemsPoData, setManageItemsPoData] = useState<PoListItem | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'table'>(() => {
    const saved = localStorage.getItem('po_view_mode');
    return saved === 'table' ? 'table' : 'card';
  });

  useEffect(() => {
    localStorage.setItem('po_view_mode', viewMode);
  }, [viewMode]);

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    requestConfirm(
      'Delete Purchase Orders',
      `Delete ${selectedIds.length} selected purchase order(s)? This cannot be undone.`,
      doBulkDelete,
      'Delete',
      'bg-red-600'
    );
  };

  const doBulkDelete = async () => {
    for (const id of selectedIds) {
      try {
        await dispatch(deletePurchaseOrder(id)).unwrap();
      } catch (err: any) {
        toast.error(`Order #${id}: ${err || 'Failed to delete'}`);
      }
    }
    toast.success('Selected purchase orders deleted');
    dispatch(clearSelectedIds());
  };

  const handleBulkSubmit = async () => {
    if (selectedIds.length === 0 || !canBulkSubmit) return;
    requestConfirm(
      'Submit for Approval',
      `Submit ${selectedIds.length} pending order(s) for approval?`,
      doBulkSubmit,
      'Submit',
      'bg-indigo-600'
    );
  };

  const doBulkSubmit = async () => {
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        await dispatch(submitPurchaseOrder(id)).unwrap();
        successCount++;
      } catch (err: any) {
        toast.error(`Order #${id}: ${err || 'Failed to submit'}`);
      }
    }
    if (successCount > 0) toast.success(`${successCount} order(s) submitted for approval`);
    dispatch(clearSelectedIds());
    dispatch(fetchPurchaseOrders(filters));
  };

  const handleBulkUnsubmit = async () => {
    if (selectedIds.length === 0 || !canBulkUnsubmit) return;
    requestConfirm(
      'Un-Submit Orders',
      `Revert ${selectedIds.length} submitted order(s) back to draft?`,
      doBulkUnsubmit,
      'Un-Submit',
      'bg-amber-600'
    );
  };

  const doBulkUnsubmit = async () => {
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        await dispatch(unsubmitPurchaseOrder(id)).unwrap();
        successCount++;
      } catch (err: any) {
        toast.error(`Order #${id}: ${err || 'Failed to unsubmit'}`);
      }
    }
    if (successCount > 0) toast.success(`${successCount} order(s) unsubmitted`);
    dispatch(clearSelectedIds());
    dispatch(fetchPurchaseOrders(filters));
  };

  const handleRejectAtr = async () => {
    if (selectedIds.length === 0 || !canRejectAtr || !rejectAtrComment.trim()) return;
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        await dispatch(rejectAtrPurchaseOrder({ id, comment: rejectAtrComment })).unwrap();
        successCount++;
      } catch (err: any) {
        toast.error(`Order #${id}: ${err || 'Failed to reject ATR'}`);
      }
    }
    if (successCount > 0) toast.success(`${successCount} ATR(s) rejected`);
    setShowRejectAtrModal(false);
    setRejectAtrComment('');
    dispatch(clearSelectedIds());
    dispatch(fetchPurchaseOrders(filters));
  };

  const handleBulkCancel = async () => {
    if (selectedIds.length === 0 || !canBulkCancel || !bulkCancelReason.trim()) return;
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        await dispatch(cancelPurchaseOrder({ id, reason: bulkCancelReason, recreate: bulkCancelRecreate === '1' })).unwrap();
        successCount++;
      } catch (err: any) {
        toast.error(`Order #${id}: ${err || 'Failed to request cancellation'}`);
      }
    }
    if (successCount > 0) toast.success(`${successCount} cancellation request(s) sent for approval`);
    setShowBulkCancelModal(false);
    setBulkCancelReason('');
    setBulkCancelRecreate('0');
    dispatch(clearSelectedIds());
    dispatch(fetchPurchaseOrders(filters));
  };

  const handleRecreate = () => {
    if (selectedIds.length !== 1 || !canRecreate) return;
    requestConfirm(
      'Recreate Order',
      'Recreate this cancelled order as a new pending order?',
      doRecreate,
      'Recreate',
      'bg-green-600'
    );
  };

  const doRecreate = async () => {
    try {
      await dispatch(recreatePurchaseOrder(selectedIds[0])).unwrap();
      toast.success('Order recreated as a new pending order');
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to recreate order');
    }
  };

  const handleBulkSplit = async () => {
    if (!canBulkSplit) return;
    requestConfirm(
      'Split Order',
      'Split this multi-store order into separate orders per store?',
      doBulkSplit,
      'Split',
      'bg-cyan-600'
    );
  };

  const doBulkSplit = async () => {
    try {
      await dispatch(splitPurchaseOrder(selectedIds[0])).unwrap();
      toast.success('Order split successfully');
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to split');
    }
  };

  const handleBulkMerge = async () => {
    if (!canBulkMerge) return;
    if (!mainOrderId) {
      toast.error('Select a main order to merge into');
      return;
    }
    try {
      await dispatch(mergePurchaseOrders({ mainOrderId, orderIds: selectedIds })).unwrap();
      toast.success('Orders merged successfully');
      setShowMergeModal(false);
      setMainOrderId(null);
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to merge');
    }
  };

  const handleSyncJobCards = async () => {
    setSyncLoading(true);
    try {
      const result = await dispatch(syncJobCards()).unwrap();
      toast.success(result.message || 'Job cards synced successfully');
    } catch (err: any) {
      toast.error(err || 'Failed to sync job cards');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSyncTechnicians = async () => {
    setSyncLoading(true);
    try {
      const result = await dispatch(syncTechnicians()).unwrap();
      toast.success(result.message || 'Technicians synced successfully');
      setIsAdminActionsOpen(false);
    } catch (err: any) {
      toast.error(err || 'Failed to sync technicians');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCreateTbaJobcards = async () => {
    setSyncLoading(true);
    try {
      const result = await dispatch(createTbaJobcards()).unwrap();
      toast.success(result.message || 'TBA jobcards created successfully');
      setIsAdminActionsOpen(false);
    } catch (err: any) {
      toast.error(err || 'Failed to create TBA jobcards');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleTbaSubmit = async () => {
    if (!tbaApprover) {
      toast.error('Please select a TBA approver');
      return;
    }
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        await dispatch(sendSupplier({ id, tbaApprover })).unwrap();
        successCount++;
      } catch (err: any) {
        toast.error(`Order #${id}: ${err || 'Failed'}`);
      }
    }
    if (successCount > 0) toast.success(`${successCount} order(s) sent for TBA assessment`);
    setShowTbaModal(false);
    setTbaApprover(null);
    dispatch(clearSelectedIds());
    dispatch(fetchPurchaseOrders(filters));
  };

  const handleBulkSetType = async (isRepair: number) => {
    requestConfirm(
      'Set Purchase Type',
      `Mark ${selectedIds.length} order(s) as ${isRepair === 1 ? 'Repair' : 'New Purchase'}?`,
      () => doBulkSetType(isRepair),
      'Set Type',
      'bg-slate-700'
    );
  };

  const doBulkSetType = async (isRepair: number) => {
    try {
      const result = await dispatch(bulkSetType({ orderIds: selectedIds, isRepair })).unwrap();
      toast.success(result.message || 'Type updated');
      setShowActionsDropdown(false);
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to set type');
    }
  };

  const handleBulkSetQuotationVat = async (isInclusive: number) => {
    requestConfirm(
      'Set Quotation VAT',
      `Set quotation VAT as ${isInclusive === 1 ? 'Inclusive' : 'Exclusive'} for ${selectedIds.length} order(s)?`,
      () => doBulkSetQuotationVat(isInclusive),
      'Set VAT',
      'bg-slate-700'
    );
  };

  const doBulkSetQuotationVat = async (isInclusive: number) => {
    try {
      const result = await dispatch(bulkSetQuotationVat({ orderIds: selectedIds, isInclusive })).unwrap();
      toast.success(result.message || 'Quotation VAT updated');
      setShowActionsDropdown(false);
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to set quotation VAT');
    }
  };

  const handleBulkSetItemOrder = async () => {
    requestConfirm(
      'Toggle Item Order',
      `Toggle item order status for ${selectedIds.length} order(s)?`,
      doBulkSetItemOrder,
      'Toggle',
      'bg-slate-700'
    );
  };

  const doBulkSetItemOrder = async () => {
    try {
      const result = await dispatch(bulkSetItemOrder(selectedIds)).unwrap();
      toast.success(result.message || 'Item order toggled');
      setShowActionsDropdown(false);
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to set item order');
    }
  };

  const handleBulkMarkNonVat = async () => {
    requestConfirm(
      'Mark as Non-VAT',
      `Mark ${selectedIds.length} order(s) as Non-VAT?`,
      doBulkMarkNonVat,
      'Mark Non-VAT',
      'bg-yellow-600'
    );
  };

  const doBulkMarkNonVat = async () => {
    try {
      const result = await dispatch(bulkMarkNonVat(selectedIds)).unwrap();
      toast.success(result.message || 'Marked as Non-VAT');
      setShowActionsDropdown(false);
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to mark as Non-VAT');
    }
  };

  const handleBulkSubmitPayment = async () => {
    requestConfirm(
      'Submit for Payment',
      `Submit ${selectedIds.length} approved order(s) with invoices for payment?`,
      doBulkSubmitPayment,
      'Submit',
      'bg-green-600'
    );
  };

  const doBulkSubmitPayment = async () => {
    try {
      const result = await dispatch(bulkSubmitPayment(selectedIds)).unwrap();
      toast.success(result.message || 'Submitted for payment');
      setShowActionsDropdown(false);
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to submit for payment');
    }
  };

  const handleFulfill = async (fulfillmentId: number) => {
    if (selectedIds.length !== 1) return;
    const fulfillmentName = (filtersData?.fulfillments || []).find(f => f.id === fulfillmentId)?.name || 'this status';
    requestConfirm(
      'Set Fulfillment Status',
      `Set fulfillment to "${fulfillmentName}" for this order?`,
      () => doFulfill(fulfillmentId),
      'Set Status',
      'bg-blue-600'
    );
  };

  const doFulfill = async (fulfillmentId: number) => {
    try {
      await dispatch(fulfillPurchaseOrder({ id: selectedIds[0], fulfillmentId })).unwrap();
      toast.success('Fulfillment status updated');
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to update fulfillment');
    }
  };

  const handleReceiveStock = async () => {
    if (selectedIds.length !== 1) return;
    try {
      await dispatch(receiveStock({ id: selectedIds[0], receivedAt: receiveDate })).unwrap();
      toast.success('Stock received successfully');
      setShowReceiveModal(false);
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to receive stock');
    }
  };

  const handleUndoReceive = async () => {
    if (selectedIds.length !== 1) return;
    requestConfirm(
      'Undo Receive',
      'Reopen the stock receipt for this order? This will reverse the received status.',
      doUndoReceive,
      'Undo Receive',
      'bg-red-600'
    );
  };

  const doUndoReceive = async () => {
    try {
      await dispatch(undoReceive(selectedIds[0])).unwrap();
      toast.success('Stock receipt undone');
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to undo receive');
    }
  };

  const handleSendToMetrofile = async () => {
    try {
      const result = await dispatch(bulkSendToMetrofile(selectedIds)).unwrap();
      toast.success(result.message || 'Documents sent to Metrofile');
      setShowMetrofileModal(false);
      dispatch(clearSelectedIds());
      dispatch(fetchPurchaseOrders(filters));
    } catch (err: any) {
      toast.error(err || 'Failed to send to Metrofile');
    }
  };

  const handleExportToMetrofile = async () => {
    requestConfirm(
      'Export to Metrofile',
      `Export ${selectedIds.length} order(s) to Metrofile CSV?`,
      doExportToMetrofile,
      'Export',
      'bg-purple-600'
    );
  };

  const doExportToMetrofile = async () => {
    try {
      const result = await dispatch(bulkExportToMetrofile(selectedIds)).unwrap();
      if (result.export_url) {
        window.open(result.export_url, '_blank');
      }
      toast.success(result.message || 'Metrofile export prepared');
      setShowActionsDropdown(false);
      dispatch(clearSelectedIds());
    } catch (err: any) {
      toast.error(err || 'Failed to export to Metrofile');
    }
  };

  const handleSageExport = async () => {
    try {
      const params: any = {};
      if (sageExportParams.date_from) params.date_from = sageExportParams.date_from;
      if (sageExportParams.date_to) params.date_to = sageExportParams.date_to;
      if (sageExportParams.order_numbers) {
        params.order_numbers = sageExportParams.order_numbers.split(',').map((s: string) => s.trim()).filter(Boolean);
      }
      // If specific orders are selected, export those
      if (selectedIds.length > 0 && !params.order_numbers?.length) {
        const selectedOrderNumbers = selectedPOs.map(po => po.order_number).filter(Boolean);
        if (selectedOrderNumbers.length > 0) {
          params.order_numbers = selectedOrderNumbers;
        }
      }
      await dispatch(downloadSageExport(params)).unwrap();
      toast.success('AP export downloaded successfully');
      setShowSageExportModal(false);
      setShowActionsDropdown(false);
      dispatch(clearSelectedIds());
    } catch (err: any) {
      toast.error(err || 'Failed to generate AP export');
    }
  };

  const handleOpenManageItems = async (poId: number) => {
    setManageItemsPoId(poId);
    setShowManageItemsModal(true);
    setShowActionsDropdown(false);
    setManageItemsLoading(true);
    setManageItemsPoData(null);
    setKeepExistingItems(false);
    setExcelFile(null);
    try {
      const [itemsResult, poDetail] = await Promise.all([
        dispatch(fetchPoItems(poId)).unwrap(),
        dispatch(fetchPurchaseOrderDetail(poId)).unwrap(),
      ]);
      setManageItemsList(itemsResult.data || []);
      setManageItemsPoData(poDetail.data || poDetail);
    } catch (err: any) {
      toast.error(err || 'Failed to load items');
    } finally {
      setManageItemsLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!manageItemsPoId || !newItem.name) return;
    try {
      const result = await dispatch(addPoItem({ poId: manageItemsPoId, data: {
        name: newItem.name,
        description: newItem.description || undefined,
        quantity: newItem.quantity ? Number(newItem.quantity) : undefined,
        price: newItem.price ? Number(newItem.price) : undefined,
        item_code: newItem.item_code || undefined,
        vat: newItem.vat,
        non_vat: newItem.non_vat,
      } })).unwrap();
      setManageItemsList([...manageItemsList, result.data]);
      setNewItem({ name: '', description: '', quantity: '', price: '', item_code: '', vat: 'inclusive', non_vat: false });
      toast.success('Item added');
    } catch (err: any) {
      toast.error(err || 'Failed to add item');
    }
  };

  const handleUpdateItem = async (itemId: number, field: string, value: any) => {
    if (!manageItemsPoId) return;
    try {
      const result = await dispatch(updatePoItem({ poId: manageItemsPoId, itemId, data: { [field]: value } })).unwrap();
      setManageItemsList(manageItemsList.map(it => it.id === itemId ? result.data : it));
    } catch (err: any) {
      toast.error(err || 'Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!manageItemsPoId) return;
    requestConfirm(
      'Remove Item',
      'Are you sure you want to remove this item?',
      () => doDeleteItem(itemId),
      'Remove',
      'bg-red-600'
    );
  };

  const doDeleteItem = async (itemId: number) => {
    try {
      await dispatch(deletePoItem({ poId: manageItemsPoId, itemId })).unwrap();
      setManageItemsList(manageItemsList.filter(it => it.id !== itemId));
      toast.success('Item removed');
    } catch (err: any) {
      toast.error(err || 'Failed to remove item');
    }
  };

  const handleLoadAtrItems = async () => {
    if (!manageItemsPoId) return;
    setAtrLoading(true);
    try {
      const result = await dispatch(loadAtrItems({ poId: manageItemsPoId, keepExisting: keepExistingItems })).unwrap();
      // The response includes the full PO with items relationship, or we can re-fetch
      const items = result.data?.items || result.data || [];
      setManageItemsList(items);
      toast.success(result.message || 'ATR items loaded successfully');
    } catch (err: any) {
      toast.error(err || 'Failed to load ATR items');
    } finally {
      setAtrLoading(false);
    }
  };

  const handleExcelImport = async () => {
    if (!manageItemsPoId || !excelFile) return;
    const quotationId = manageItemsPoData?.quotation?.id;
    if (!quotationId) {
      toast.error('No quotation found for this order. Please save a quotation first before importing Excel items.');
      return;
    }
    setExcelImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('quotation_id', String(quotationId));
      formData.append('generate_po_items', '1');
      await importQuotationLineItemsApi(manageItemsPoId, formData);
      toast.success('Excel items imported successfully');
      const result = await dispatch(fetchPoItems(manageItemsPoId)).unwrap();
      setManageItemsList(result.data || []);
      setExcelFile(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err || 'Failed to import Excel');
    } finally {
      setExcelImportLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadExcelTemplateApi();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'po_items_template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error('Failed to download template');
    }
  };

  // Compute totals from manage items list
  const itemsSubtotal = manageItemsList.reduce((sum: number, item: any) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    return sum + (qty * price);
  }, 0);
  const itemsVatAmount = manageItemsList.reduce((sum: number, item: any) => {
    if (item.non_vat) return sum;
    const qty = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const lineTotal = qty * price;
    if (item.vat === 'inclusive') return sum;
    return sum + (lineTotal * 0.15);
  }, 0);
  const itemsTotal = itemsSubtotal + itemsVatAmount;
  const quoteAmount = manageItemsPoData?.quotation ? Number(manageItemsPoData.quotation.amount) || 0 : 0;


  // Compute gating flags from selected POs (mirrors Livewire conditions)
  const selectedPOs = list.filter(po => selectedIds.includes(po.id));
  const selectedPending = selectedPOs.filter(po => po.status_id === 1);
  const selectedSubmitted = selectedPOs.filter(po => {
    const isSubmitted = typeof po.submitted === 'number' ? po.submitted !== 0 : po.submitted;
    return isSubmitted;
  });
  const selectedHasOrderNumber = selectedPOs.filter(po => !!po.order_number);
  const selectedCancelled = selectedPOs.filter(po => po.status_id === 4);
  const allPending = selectedPOs.length > 0 && selectedPOs.every(po => po.status_id === 1);
  const noneCancelled = selectedCancelled.length === 0;
  const canDeleteAny = selectedPOs.some(po => po.can_delete);

  // Gate flags
  const canBulkSubmit = selectedPending.length > 0;
  // Unsubmit: all selected must be submitted, none cancelled, none already approved (no approver with status_id = 2)
  const canBulkUnsubmit = selectedSubmitted.length > 0 &&
    selectedSubmitted.length === selectedPOs.length &&
    noneCancelled &&
    selectedPOs.every(po => !po.approvers?.some(a => a.status_id === 2));
  // Reject ATR: has linked requisition, not approved (no order_number), not cancelled
  const canRejectAtr = selectedPOs.some(po => !!po.requisition?.id) &&
    selectedPOs.every(po => !po.order_number) &&
    noneCancelled;
  const canBulkCancel = selectedHasOrderNumber.length > 0 && canDeleteAny && noneCancelled &&
    !selectedPOs.some(po => !!po.cancel_index);
  // Recreate: single cancelled order that has not been recreated yet (backend sets can_recreate)
  const canRecreate = selectedIds.length === 1 && selectedPOs[0]?.status_id === 4 && !!selectedPOs[0]?.can_recreate;
  const canBulkSplit = selectedIds.length === 1 && selectedPOs[0]?.stores?.length > 1 && selectedPOs[0]?.status_id === 1;
  const canBulkMerge = selectedIds.length >= 2 && allPending;
  // Delete: none have order_number AND none have reference_number AND can_delete
  const canBulkDelete = selectedPOs.length > 0 &&
    selectedHasOrderNumber.length === 0 &&
    selectedPOs.every(po => !po.reference_number) &&
    canDeleteAny;

  // Additional gating flags for dropdown actions (mirrors Livewire logic)
  const selectedApproved = selectedPOs.filter(po => po.status_id === 2);
  const canFulfillment = selectedIds.length === 1 && noneCancelled;
  const canSubmitForPayment = selectedApproved.length > 0 && selectedApproved.length === selectedPOs.length && noneCancelled;
  const canManageItems = selectedIds.length === 1 && selectedPOs[0]?.status_id === 2 && !!selectedPOs[0]?.is_order;
  const hasQuotation = selectedPOs.some(po => !!po.quotation);
  const canReceiveStock = selectedIds.length === 1 && (user?.is_admin || user?.user_role_id === 6);
  const canMetrofile = (user?.is_admin || user?.user_role_id === 6);

  const hasActiveFilters = !!(
    filters.search || filters.status_id || (filters.stores && filters.stores.length > 0) ||
    filters.supplier_id || filters.technician_id || filters.user_id || filters.internal !== undefined ||
    filters.is_repair !== undefined || filters.date_from || filters.date_to || filters.linked_only || filters.multi_store ||
    filters.required_grn || filters.required_dn_jbc || filters.view_pending || filters.view_submitted ||
    filters.view_tba || filters.view_approved || filters.view_rejected || filters.view_cancelled
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Purchase Orders</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {total} order{total !== 1 ? 's' : ''} total
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/purchase-order-create"
            className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create PO
          </Link>
          <button
            onClick={handleSyncJobCards}
            disabled={syncLoading}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {syncLoading ? 'Syncing...' : 'Sync Job Cards'}
          </button>
          {user?.is_admin && (
            <div ref={setAdminActionsRef} className="relative">
              <button
                onClick={() => setIsAdminActionsOpen(!isAdminActionsOpen)}
                className="px-3 py-2 bg-slate-700 text-white rounded-lg text-sm hover:bg-slate-600 flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                Admin Actions
                <svg className={`w-3 h-3 transition-transform ${isAdminActionsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isAdminActionsOpen && (
                <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-20 py-1">
                  <button
                    onClick={handleSyncTechnicians}
                    disabled={syncLoading}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {syncLoading ? 'Syncing...' : 'Sync Technicians'}
                  </button>
                  <button
                    onClick={handleCreateTbaJobcards}
                    disabled={syncLoading}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    {syncLoading ? 'Creating...' : 'Create TBA Jobcards'}
                  </button>
                  <button
                    onClick={() => { setShowSageExportModal(true); setIsAdminActionsOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    AP Export
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Status Count Cards */}
      {!!statusCounts && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {[
            { label: 'Pending', count: statusCounts.pending, gradient: 'bg-gradient-to-br from-[#667eea] to-[#764ba2]', icon: <ClockIcon />, filterKey: 'view_pending' as const },
            { label: 'Submitted', count: statusCounts.submitted_pending, gradient: 'bg-gradient-to-br from-[#17a2b8] to-[#0dcaf0]', icon: <PaperPlaneIcon />, filterKey: 'view_submitted' as const },
            { label: 'TBA', count: statusCounts.tba, gradient: 'bg-gradient-to-br from-[#26408B] to-[#3d5afe]', icon: <PaperPlaneIcon />, filterKey: 'view_tba' as const },
            { label: 'Approved', count: statusCounts.approved, gradient: 'bg-gradient-to-br from-[#198754] to-[#20c997]', icon: <CheckDoubleIcon />, filterKey: 'view_approved' as const },
            { label: 'Rejected', count: statusCounts.rejected, gradient: 'bg-gradient-to-br from-[#dc3545] to-[#f8719d]', icon: <TimesIcon />, filterKey: 'view_rejected' as const },
            { label: 'Cancelled', count: statusCounts.cancelled, gradient: 'bg-gradient-to-br from-[#4F4F4F] to-[#6c757d]', icon: <BanIcon />, filterKey: 'view_cancelled' as const },
          ].map((stat) => {
            const isActive = !!filters[stat.filterKey];
            return (
              <button
                key={stat.label}
                onClick={() => handleFilterChange(stat.filterKey, isActive ? undefined : true)}
                className={`p-3 rounded-lg shadow-sm hover:shadow-md transition text-left info-card ${stat.gradient} ${isActive ? 'ring-4 ring-yellow-400' : 'ring-0'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-white/70 text-xs flex items-center gap-1">
                    {stat.icon}
                    {stat.label}
                  </span>
                  <span className="text-white font-bold text-3xl">{stat.count}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Search Bar - live search with debounce */}
      <div className="flex gap-2">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search order #, reference #, requestor, supplier, technician, store, GRN, quote, invoice..."
          className="flex-1 px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
        />
        <button
          type="button"
          onClick={handleResetFilters}
          disabled={!hasActiveFilters}
          className="px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm disabled:opacity-40"
        >
          Reset
        </button>
      </div>

      {/* Basic Filters Row - always visible */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Stores - searchable multi-select */}
        <div className="relative" ref={setStoreDropdownRef}>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
            Stores {selectedStoreIds.length > 0 && `(${selectedStoreIds.length} selected)`}
          </label>
          <input
            type="text"
            placeholder="Search stores..."
            value={storeSearchTerm}
            onChange={(e) => setStoreSearchTerm(e.target.value)}
            onFocus={() => setIsStoreDropdownOpen(true)}
            className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
          />
          {isStoreDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-slate-200 dark:border-slate-700 max-h-48 overflow-auto">
              {filteredStores.length > 0 ? (
                filteredStores.map(store => (
                  <div
                    key={store.id}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer flex items-center text-sm text-slate-900 dark:text-slate-100"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleStoreToggle(store.id);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStoreIds.includes(store.id)}
                      readOnly
                      className="mr-2 h-4 w-4 rounded"
                    />
                    {store.name}{store.storenumber && ` (${store.storenumber})`}
                  </div>
                ))
              ) : (
                <div className="p-2 text-slate-500 text-sm">No stores found</div>
              )}
            </div>
          )}
          {selectedStoreIds.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {selectedStoreIds.map(storeId => {
                const store = (filtersData?.stores || []).find(s => s.id === storeId);
                return store ? (
                  <span
                    key={storeId}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-100"
                  >
                    {store.name}{store.storenumber && ` (${store.storenumber})`}
                    <button
                      type="button"
                      className="ml-1.5 inline-flex text-blue-500 hover:text-blue-700"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleStoreToggle(storeId);
                      }}
                    >
                      &times;
                    </button>
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>

        {/* Status */}
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Order Status</label>
          <select
            value={filters.status_id || ''}
            onChange={(e) => handleFilterChange('status_id', e.target.value ? Number(e.target.value) : undefined)}
            className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
          >
            <option value="">All Statuses</option>
            {(filtersData?.statuses || []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* Date */}
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Date</label>
          <input
            type="date"
            value={filters.date_to || ''}
            onChange={(e) => handleFilterChange('date_to', e.target.value || undefined)}
            className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
          />
        </div>

        {/* Filters Toggle Button */}
        <div className="flex items-end">
          <button
            type="button"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="w-full mt-1 px-3 py-2 rounded-lg border bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 text-sm flex items-center justify-center gap-2 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            More Filters
          </button>
        </div>
      </div>

      {/* Advanced Filters Collapsible Section */}
      {showAdvancedFilters && (
        <div className="p-4 rounded-lg border bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Supplier - searchable single-select */}
            <div className="relative" ref={setSupplierDropdownRef}>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Supplier</label>
              <input
                type="text"
                placeholder={selectedSupplier ? selectedSupplier.name : 'All Suppliers'}
                value={supplierSearchTerm}
                onChange={(e) => setSupplierSearchTerm(e.target.value)}
                onFocus={() => setIsSupplierDropdownOpen(true)}
                className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              />
              {isSupplierDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-slate-200 dark:border-slate-700 max-h-48 overflow-auto">
                  <div
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer text-sm text-slate-900 dark:text-slate-100"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFilterChange('supplier_id', undefined);
                      setSupplierSearchTerm('');
                      setIsSupplierDropdownOpen(false);
                    }}
                  >
                    All Suppliers
                  </div>
                  {filteredSuppliers.map(s => (
                    <div
                      key={s.id}
                      className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer text-sm text-slate-900 dark:text-slate-100"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFilterChange('supplier_id', s.id);
                        setSupplierSearchTerm('');
                        setIsSupplierDropdownOpen(false);
                      }}
                    >
                      {s.name}
                    </div>
                  ))}
                </div>
              )}
              {selectedSupplier && !isSupplierDropdownOpen && (
                <div className="mt-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-100">
                    {selectedSupplier.name}
                    <button
                      type="button"
                      className="ml-1.5 inline-flex text-blue-500 hover:text-blue-700"
                      onClick={(e) => {
                        e.preventDefault();
                        handleFilterChange('supplier_id', undefined);
                      }}
                    >
                      &times;
                    </button>
                  </span>
                </div>
              )}
            </div>

            {/* Technician */}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Technician</label>
              <select
                value={filters.technician_id || ''}
                onChange={(e) => handleFilterChange('technician_id', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              >
                <option value="">All Technicians</option>
                {(filtersData?.technicians || []).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {/* Requester */}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Requester</label>
              <select
                value={filters.user_id || ''}
                onChange={(e) => handleFilterChange('user_id', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              >
                <option value="">All Users</option>
                {(filtersData?.users || []).map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>

            {/* Type */}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Type</label>
              <select
                value={filters.internal ?? ''}
                onChange={(e) => handleFilterChange('internal', e.target.value === '' ? undefined : Number(e.target.value))}
                className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              >
                <option value="">All Types</option>
                <option value="1">Internal</option>
                <option value="2">External</option>
              </select>
            </div>

            {/* Purchase Type (New Purchase / Repair) */}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Purchase Type</label>
              <select
                value={filters.is_repair ?? ''}
                onChange={(e) => handleFilterChange('is_repair', e.target.value === '' ? undefined : Number(e.target.value))}
                className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              >
                <option value="">All Purchase Types</option>
                <option value="0">New Purchase</option>
                <option value="1">Repair</option>
              </select>
            </div>

            {/* Date From */}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Date From</label>
              <input
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => handleFilterChange('date_from', e.target.value || undefined)}
                className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Date To</label>
              <input
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => handleFilterChange('date_to', e.target.value || undefined)}
                className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              />
            </div>

            {/* Category */}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Category</label>
              <select
                value={filters.category_id || ''}
                onChange={(e) => {
                  handleFilterChange('category_id', e.target.value ? Number(e.target.value) : undefined);
                  handleFilterChange('subcategory_id', undefined);
                }}
                className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              >
                <option value="">All Categories</option>
                {(filtersData?.categories || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Subcategory (dependent on Category) */}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Subcategory</label>
              <select
                value={filters.subcategory_id || ''}
                onChange={(e) => handleFilterChange('subcategory_id', e.target.value ? Number(e.target.value) : undefined)}
                disabled={!filters.category_id}
                className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm disabled:opacity-50"
              >
                <option value="">{filters.category_id ? 'All Subcategories' : 'Select a category first'}</option>
                {(filtersData?.subcategories || [])
                  .filter((s) => s.requisition_category_id === filters.category_id)
                  .map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
              </select>
            </div>


          </div>

          {/* Advanced Filters Actions */}
          <div className="flex justify-center gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={handleResetFilters}
              className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              Reset Filters
            </button>
            <button
              type="button"
              onClick={() => setShowAdvancedFilters(false)}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Checkbox Filters + View Mode Toggle - always visible */}
      <div className="flex flex-wrap items-center justify-between gap-3 py-1">
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={filters.linked_only || false}
              onChange={(e) => handleFilterChange('linked_only', e.target.checked || undefined)}
              className="rounded"
            />
            Linked Orders Only
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={filters.multi_store || false}
              onChange={(e) => handleFilterChange('multi_store', e.target.checked || undefined)}
              className="rounded"
            />
            Multi-Store
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={filters.required_grn || false}
              onChange={(e) => handleFilterChange('required_grn', e.target.checked || undefined)}
              className="rounded"
            />
            GRN Required
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={filters.required_dn_jbc || false}
              onChange={(e) => handleFilterChange('required_dn_jbc', e.target.checked || undefined)}
              className="rounded"
            />
            DN/JBC Required
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={filters.view_fulfilled || false}
              onChange={(e) => handleFilterChange('view_fulfilled', e.target.checked || undefined)}
              className="rounded"
            />
            Fulfilled
          </label>
        </div>
        <div className="flex rounded-lg border border-slate-300 dark:border-slate-700 overflow-hidden">
          <button
            onClick={() => setViewMode('card')}
            className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition ${viewMode === 'card' ? 'bg-brand-500 text-white' : 'bg-white dark:bg-slate-900/50 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
            title="Card view"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 10h16M4 14h16" />
            </svg>
            Cards
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition ${viewMode === 'table' ? 'bg-brand-500 text-white' : 'bg-white dark:bg-slate-900/50 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
            title="Table view"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            Table
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {!!(selectedIds.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <span className="text-sm font-medium">{selectedIds.length} selected</span>

          {/* Grouped Actions Dropdown - first for easy access */}
          <div ref={actionsDropdownRef} className="relative">
            <button
              onClick={() => setShowActionsDropdown(!showActionsDropdown)}
              className="px-3 py-1.5 rounded-lg text-sm border bg-slate-700 text-white border-slate-600 hover:bg-slate-600 flex items-center gap-2"
            >
              Actions
              <svg className={`w-3 h-3 transition-transform ${showActionsDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showActionsDropdown && (
              <div className="absolute left-0 mt-1 w-64 max-h-96 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-20 py-1">
                {/* TBA Section - only if none cancelled and not all approved */}
                {noneCancelled && selectedPOs.some(po => !po.order_number) && (
                  <>
                    <div className="px-3 py-1 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide">TBA</div>
                    <button
                      onClick={() => { setShowTbaModal(true); setShowActionsDropdown(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Submit for TBA
                    </button>
                  </>
                )}

                {/* Type Section - always available when orders selected */}
                <div className="px-3 py-1 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide border-t border-slate-100 dark:border-slate-700 mt-1">Set Type</div>
                <button
                  onClick={() => handleBulkSetType(1)}
                  className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  Set as Repair
                </button>
                <button
                  onClick={() => handleBulkSetType(0)}
                  className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  Set as New Purchase
                </button>

                {/* VAT Section - only if at least one selected has a quotation */}
                {hasQuotation && (
                  <>
                    <div className="px-3 py-1 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide border-t border-slate-100 dark:border-slate-700 mt-1">Quotation VAT</div>
                    <button
                      onClick={() => handleBulkSetQuotationVat(1)}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Set as Inclusive
                    </button>
                    <button
                      onClick={() => handleBulkSetQuotationVat(0)}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Set as Exclusive
                    </button>
                  </>
                )}
                <button
                  onClick={handleBulkMarkNonVat}
                  className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  Mark as Non-VAT
                </button>

                {/* Item Order - always available when orders selected */}
                <div className="px-3 py-1 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide border-t border-slate-100 dark:border-slate-700 mt-1">Items</div>
                <button
                  onClick={handleBulkSetItemOrder}
                  className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  Toggle Item Order
                </button>
                {canManageItems && (
                  <button
                    onClick={() => handleOpenManageItems(selectedIds[0])}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    Manage Items
                  </button>
                )}

                {/* Stock Section - only for admin/IT with 1 selected */}
                {canReceiveStock && (
                  <>
                    <div className="px-3 py-1 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide border-t border-slate-100 dark:border-slate-700 mt-1">Stock</div>
                    <button
                      onClick={() => { setShowReceiveModal(true); setShowActionsDropdown(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Receive Stock
                    </button>
                    <button
                      onClick={() => { handleUndoReceive(); setShowActionsDropdown(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Undo Receive
                    </button>
                  </>
                )}

                {/* Payment Section - only if all approved with invoices, none cancelled */}
                {canSubmitForPayment && (
                  <>
                    <div className="px-3 py-1 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide border-t border-slate-100 dark:border-slate-700 mt-1">Payment</div>
                    <button
                      onClick={handleBulkSubmitPayment}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Submit for Payment
                    </button>
                  </>
                )}

                {/* Recreate Section - only for a single cancelled order not yet recreated */}
                {canRecreate && (
                  <>
                    <div className="px-3 py-1 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide border-t border-slate-100 dark:border-slate-700 mt-1">Order Management</div>
                    <button
                      onClick={() => { handleRecreate(); setShowActionsDropdown(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Recreate Order
                    </button>
                  </>
                )}

                {/* Metrofile Section - only for admin/finance */}
                {canMetrofile && (
                  <>
                    <div className="px-3 py-1 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide border-t border-slate-100 dark:border-slate-700 mt-1">Metrofile</div>
                    <button
                      onClick={() => { setShowMetrofileModal(true); setShowActionsDropdown(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Send to Metrofile
                    </button>
                    <button
                      onClick={handleExportToMetrofile}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Export to Metrofile
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* View/Edit/Delete for single selection */}
          {selectedIds.length === 1 && (
            <>
              <Link
                to={`/purchase-orders/${selectedIds[0]}`}
                className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
              >
                View
              </Link>
              <Link
                to={`/purchase-orders/${selectedIds[0]}/edit`}
                className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Edit
              </Link>
              <button
                onClick={() => handleDelete(selectedIds[0])}
                disabled={actionLoading}
                className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 disabled:opacity-50"
              >
                Delete
              </button>
            </>
          )}

          {/* Fulfillment Status - only if 1 selected and not cancelled */}
          {canFulfillment && (
            <select
              value={selectedPOs[0]?.fulfillment?.id || ''}
              onChange={(e) => { if (e.target.value) handleFulfill(Number(e.target.value)); }}
              className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option value="">Fulfillment Status</option>
              {(filtersData?.fulfillments || []).map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          )}

          {/* Quick actions */}
          {canBulkSubmit && (
            <button
              onClick={handleBulkSubmit}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-indigo-500 text-white rounded-lg text-sm hover:bg-indigo-600 disabled:opacity-50"
            >
              Submit
            </button>
          )}
          {canBulkUnsubmit && (
            <button
              onClick={handleBulkUnsubmit}
              disabled={actionLoading}
              className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 disabled:opacity-50"
            >
              Unsubmit
            </button>
          )}
          {canRejectAtr && (
            <button
              onClick={() => setShowRejectAtrModal(true)}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 disabled:opacity-50"
            >
              Reject ATR
            </button>
          )}
          {canBulkCancel && (
            <button
              onClick={() => setShowBulkCancelModal(true)}
              disabled={actionLoading}
              className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          {canBulkSplit && (
            <button
              onClick={handleBulkSplit}
              disabled={actionLoading}
              className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 disabled:opacity-50"
              title="Split order with multiple stores"
            >
              Split
            </button>
          )}
          {canBulkMerge && (
            <button
              onClick={() => setShowMergeModal(true)}
              disabled={actionLoading}
              className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 disabled:opacity-50"
              title="Merge pending orders"
            >
              Merge
            </button>
          )}
          {canBulkDelete && (
            <button
              onClick={handleBulkDelete}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 disabled:opacity-50"
            >
              Delete
            </button>
          )}

          <div className="flex-1" />

          <button
            onClick={() => dispatch(clearSelectedIds())}
            className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Error */}
      {!!error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {!!loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-400"></div>
        </div>
      )}

      {/* List */}
      {!!(!loading && list.length === 0) && (
        <div className="text-center py-12 rounded-lg border bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <p className="text-slate-500 dark:text-slate-400">No purchase orders found.</p>
        </div>
      )}

      {!!(!loading && list.length > 0) && viewMode === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((po) => {
            const status = getPoStatusDisplay(po);
            const isSelected = selectedIds.includes(po.id);
            const rowBg = getPoRowColor(po);
            const canEdit = !!po.can_edit;
            const canSelect = !!po.can_select;
            const canDelete = !!po.can_delete;
            const isSubmitted = typeof po.submitted === 'number' ? po.submitted !== 0 : po.submitted;
            const labelColor = (isSubmitted && !po.order_number && !po.reference_number) ? 'text-slate-900' : 'text-white/60';
            return (
              <div
                key={po.id}
                style={{ backgroundColor: rowBg }}
                className={`p-4 rounded-lg text-white transition ${isSelected ? 'ring-2 ring-white' : ''}`}
              >
                <div className="flex items-start gap-3">
                  {!!canSelect && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => dispatch(toggleSelectedId(po.id))}
                      className="mt-1 rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/purchase-orders/${po.id}`}
                        className="font-semibold text-lg text-white underline decoration-white/40 hover:decoration-white"
                      >
                        {(po.order_number || po.reference_number) ? '' : 'PO'}
                      </Link>
                      {!!po.reference_number && !po.order_number && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-400/90 text-yellow-900">
                          Ref: {po.reference_number}
                        </span>
                      )}
                      {!!po.order_number && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-400/90 text-yellow-900">
                          #{po.order_number}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.textColor}`}>
                        {status.text}
                      </span>
                      {!!po.is_linked && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                          Linked
                        </span>
                      )}
                      {!!(po.internal === 1) && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                          Internal
                        </span>
                      )}
                      {!!(po.internal === 2) && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                          External
                        </span>
                      )}
                      {!!po.requisition?.id && (
                        <Link
                          to={`/requisitions/${po.requisition.id}`}
                          className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/80 hover:bg-purple-500 text-white transition cursor-pointer"
                          title={`View Requisition #${po.requisition.id}`}
                        >
                          ATR #{po.requisition.id}
                        </Link>
                      )}
                      {!!po.non_vat_display && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-600/80 text-white" title="Non-VAT supplier">
                          Non-VAT
                        </span>
                      )}
                      {!!po.is_amended && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/90 text-amber-900" title="Price amended via GRN variance or amendment approval">
                          Amended
                        </span>
                      )}
                      {po.grn_status === 'not_received' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-600/80 text-white" title="No GRN received yet">
                          Not Received
                        </span>
                      )}
                      {po.grn_status === 'received_fully' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-700/80 text-white" title="All items received">
                          Received Fully
                        </span>
                      )}
                      {po.grn_status === 'partially_received' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-600/80 text-white" title="Some items still outstanding">
                          Partially Received
                        </span>
                      )}
                      {!!po.documents_required && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-600/80 text-white animate-pulse" title={`${po.documents_required} Required`}>
                          {po.documents_required} Required
                        </span>
                      )}
                      {!!po.is_fulfilled && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/90 text-emerald-900" title="Order has been fulfilled">
                          Fulfilled
                        </span>
                      )}
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div>
                        <span className={`${labelColor} text-xs`}>Store:</span>{' '}
                        <span className="font-medium text-yellow-300">
                          <StoreList stores={po.stores || []} />
                        </span>
                      </div>
                      <div>
                        <span className={`${labelColor} text-xs`}>Date:</span>{' '}
                        <span className="font-medium">{formatDate(po.date)}</span>
                      </div>
                      <div>
                        <span className={`${labelColor} text-xs`}>{po.internal === 1 ? 'Technician:' : 'Supplier:'}</span>{' '}
                        <span className="font-medium text-yellow-300">
                          {po.internal === 1 ? (po.technician?.name || 'N/A') : (po.supplier?.name || 'N/A')}
                        </span>
                      </div>
                      <div>
                        <span className={`${labelColor} text-xs`}>Requested by:</span>{' '}
                        <span className="font-medium">{po.user?.name || 'N/A'}</span>
                      </div>
                      <div className="col-span-2">
                        <span className={`${labelColor} text-xs`}>Category:</span>{' '}
                        <span className="font-medium text-yellow-300">
                          {po.category?.name || 'N/A'}{po.subCategory?.name ? ` - ${po.subCategory.name}` : ''}
                        </span>
                        <span className="ml-3">
                          <span className={`${labelColor} text-xs`}>Type:</span>{' '}
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-400/90 text-yellow-900">
                            {po.is_repair === 1 ? 'Repair' : 'New Purchase'}
                          </span>
                        </span>
                      </div>
                    </div>

                    {(!!po.quotation || (po.invoices && po.invoices.length > 0)) && (
                      <div className="mt-1 text-sm">
                        {!!po.quotation && (
                          <>
                            <span className={`${labelColor} text-xs`}>Quote:</span>{' '}
                            <span className="font-medium text-yellow-300">{po.quotation.quotation_number} - {formatCurrency(po.quotation.amount)}</span>
                          </>
                        )}
                        {(po.invoices && po.invoices.length > 0) && (
                          <>
                            {!!po.quotation && <span className="mx-2">|</span>}
                            <span className={`${labelColor} text-xs`}>Invoice({po.invoices.length}):</span>{' '}
                            <span className="font-medium text-yellow-300">
                              {formatCurrency(po.invoices.reduce((sum: number, inv: any) => sum + (parseFloat(inv.amount) || 0), 0))}
                            </span>
                          </>
                        )}
                      </div>
                    )}

                    {/* Items to Repair / Request - lazy loaded */}
                    <PoItemsPreview poId={po.id} />

                    <div className="mt-2 flex items-center gap-3 text-xs text-white/60">
                      <span className={labelColor}>Updated:</span> {formatDateTime(po.updated_at)}
                      {!!(po.grns && po.grns.length > 0) && (
                        <span className="px-2 py-0.5 rounded-full bg-yellow-400/90 text-yellow-900 font-medium">GRNs: {po.grns!.length}</span>
                      )}
                      {getDocumentCount(po) > 0 && (
                        <button
                          type="button"
                          onClick={() => handleViewDocuments(po.id)}
                          className="px-2 py-0.5 rounded-full bg-indigo-500/90 text-white font-medium hover:bg-indigo-600 transition-colors"
                        >
                          View Documents ({getDocumentCount(po)})
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleAddDocument(po.id)}
                        className="px-2 py-0.5 rounded-full bg-green-500/90 text-white font-medium hover:bg-green-600 transition-colors"
                      >
                        Add Document
                      </button>
                    </div>

                    {/* Approval chain and action buttons in same container */}
                    <div className="mt-2 pt-2 border-t border-white/20 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        {!!(po.approvers && po.approvers.length > 0) && (
                          <div>
                            <div className={`text-xs ${labelColor} mb-1`}>Approval Chain:</div>
                            <div className="flex flex-wrap gap-1">
                              {po.approvers.map((approver: any) => {
                                let statusColor = 'bg-white/20';
                                let statusText = 'Pending';
                                let icon = <ClockIcon />;
                                if (approver.status_id === 2) { statusColor = 'bg-green-500/80'; statusText = 'Approved'; icon = <CheckDoubleIcon />; }
                                else if (approver.status_id === 3) { statusColor = 'bg-red-500/80'; statusText = 'Rejected'; icon = <TimesIcon />; }
                                return (
                                  <span
                                    key={approver.id}
                                    className={`px-2 py-0.5 rounded-full text-xs ${statusColor} text-white flex items-center gap-1`}
                                    title={`${approver.user?.name || 'Unknown'} - ${statusText}`}
                                  >
                                    {icon}
                                    {approver.user?.name || 'Unknown'}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 sm:justify-end">
                        <Link
                          to={`/purchase-orders/${po.id}`}
                          className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded text-xs transition"
                        >
                          View
                        </Link>
                        {!!(canEdit && !po.order_number) && (
                          <Link
                            to={`/purchase-orders/${po.id}/edit`}
                            className="px-3 py-1.5 bg-blue-500/80 hover:bg-blue-500 text-white rounded text-xs transition"
                          >
                            Edit
                          </Link>
                        )}
                        {!!canDelete && (
                          <button
                            onClick={() => handleDelete(po.id)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded text-xs transition disabled:opacity-50"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table View */}
      {!!(!loading && list.length > 0) && viewMode === 'table' && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50">
          <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: '0 6px' }}>
            <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
              <tr className="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2">Order #</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Store(s)</th>
                <th className="px-3 py-2">Supplier/Technician</th>
                <th className="px-3 py-2">Requested By</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2">Documents</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((po) => {
                const status = getPoStatusDisplay(po);
                const isSelected = selectedIds.includes(po.id);
                const canSelect = !!po.can_select;
                const canEdit = !!po.can_edit;
                const canDelete = !!po.can_delete;
                const rowBg = getPoRowColor(po);
                const poTotal = po.invoices && po.invoices.length > 0
                  ? po.invoices.reduce((sum: number, inv: any) => sum + (Number(inv.amount) || 0), 0)
                  : po.quotation ? Number(po.quotation.amount) || 0 : 0;
                return (
                  <tr
                    key={po.id}
                    style={{ backgroundColor: rowBg }}
                    className={`transition rounded-lg ${isSelected ? 'ring-2 ring-inset ring-white' : ''}`}
                  >
                    <td className="px-3 py-2 align-middle" style={{ height: '72px' }}>
                      {!!canSelect && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => dispatch(toggleSelectedId(po.id))}
                          className="rounded"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Link to={`/purchase-orders/${po.id}`} className="text-white hover:underline font-medium">
                        {po.order_number || po.reference_number || `#${po.id}`}
                      </Link>
                      {!!po.is_linked && <span className="ml-1 text-xs text-slate-400">(Linked)</span>}
                      {!!po.is_amended && <span className="ml-1 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">Amended</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.textColor}`}>
                        {status.text}
                      </span>
                      {!!po.is_fulfilled && (
                        <span className="ml-1 px-1.5 py-0.5 rounded text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">Fulfilled</span>
                      )}
                      {po.grn_status === 'received_fully' && <span className="ml-1 px-1.5 py-0.5 rounded text-xs bg-green-600 text-white">Received</span>}
                      {po.grn_status === 'partially_received' && <span className="ml-1 px-1.5 py-0.5 rounded text-xs bg-yellow-500 text-yellow-900">Partial</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-white/90">{formatDate(po.date)}</td>
                    <td className="px-3 py-2 max-w-[160px] text-white/90">
                      <StorePopover stores={po.stores || []} className="text-white/90" />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-white/90">
                      {po.internal === 1 ? (po.technician?.name || 'N/A') : (po.supplier?.name || 'N/A')}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-white/90">{po.user?.name || 'N/A'}</td>
                    <td className="px-3 py-2 text-white/90">
                      <div className="flex items-center gap-1 flex-wrap">
                        {po.category?.name || 'N/A'}
                        <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-white/20 text-white">
                          {po.is_repair === 1 ? 'Repair' : 'Purchase'}
                        </span>
                      </div>
                      {po.subCategory?.name && (
                        <div className="text-xs text-white/60 mt-0.5">{po.subCategory.name}</div>
                      )}
                      {!!po.non_vat_display && <span className="text-xs text-yellow-300">Non-VAT</span>}
                    </td>
                    <td className="px-3 py-2 max-w-[280px] text-white/90">
                      <PoItemsPreview poId={po.id} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right font-medium text-white">
                      {poTotal > 0 ? (
                        <>
                          {formatCurrency(poTotal)}
                          <span className="ml-1 text-xs text-white/60">
                            {po.non_vat_display ? '(Non-VAT)' : (po.quotation?.vat ? '(Incl.)' : '(Excl.)')}
                          </span>
                        </>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1 flex-wrap">
                        {getDocumentCount(po) > 0 && (
                          <button
                            onClick={() => handleViewDocuments(po.id)}
                            className="px-2 py-1 text-xs bg-white/20 text-white rounded hover:bg-white/30 transition-colors font-medium"
                          >
                            View ({getDocumentCount(po)})
                          </button>
                        )}
                        <button
                          onClick={() => handleAddDocument(po.id)}
                          className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors font-medium"
                        >
                          + Add
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Link to={`/purchase-orders/${po.id}`} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">View</Link>
                        {!!(canEdit && !po.order_number) && (
                          <Link to={`/purchase-orders/${po.id}/edit`} className="px-2 py-1 text-xs bg-white/20 text-white rounded hover:bg-white/30">Edit</Link>
                        )}
                        {!!canDelete && (
                          <button onClick={() => handleDelete(po.id)} disabled={actionLoading} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50">Del</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!!(lastPage > 1) && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1.5 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            Page {currentPage} of {lastPage}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === lastPage}
            className="px-3 py-1.5 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Generic Confirmation Modal */}
      {confirmModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">{confirmModal.title}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">{confirmModal.message}</p>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleConfirmedAction}
                disabled={actionLoading}
                className={`flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 ${confirmModal.confirmColor}`}
              >
                {actionLoading ? 'Processing...' : confirmModal.confirmLabel}
              </button>
              <button
                onClick={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject ATR Modal */}
      {showRejectAtrModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">Reject ATR — {selectedIds.length} Purchase Order(s)</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              This will reject the linked ATR (requisition), reset approvers, and set the PO status to rejected.
            </p>
            <textarea
              value={rejectAtrComment}
              onChange={(e) => setRejectAtrComment(e.target.value)}
              placeholder="Reason for ATR rejection (required)"
              rows={4}
              className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleRejectAtr}
                disabled={!rejectAtrComment.trim() || actionLoading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Yes, Reject ATR'}
              </button>
              <button
                onClick={() => { setShowRejectAtrModal(false); setRejectAtrComment(''); }}
                className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Cancel Modal */}
      {showBulkCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">Request Cancellation — {selectedIds.length} Purchase Order(s)</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              A cancellation request will be sent to each order's approvers. Orders stay approved until all approvers approve.
            </p>
            <textarea
              value={bulkCancelReason}
              onChange={(e) => setBulkCancelReason(e.target.value)}
              placeholder="Reason for cancellation (required)"
              rows={4}
              className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            />
            <div className="mt-3">
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Recreate order(s) once cancelled?</label>
              <select
                value={bulkCancelRecreate}
                onChange={(e) => setBulkCancelRecreate(e.target.value as '0' | '1')}
                className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleBulkCancel}
                disabled={!bulkCancelReason.trim() || actionLoading}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Request Cancellation'}
              </button>
              <button
                onClick={() => { setShowBulkCancelModal(false); setBulkCancelReason(''); setBulkCancelRecreate('0'); }}
                className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">Merge {selectedIds.length} Purchase Orders</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">Select the main order to merge the others into:</p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {list.filter(po => selectedIds.includes(po.id)).map(po => (
                <label key={po.id} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50">
                  <input
                    type="radio"
                    name="mainOrder"
                    checked={mainOrderId === po.id}
                    onChange={() => setMainOrderId(po.id)}
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {po.order_number ? `#${po.order_number}` : `#${po.id}`} — {po.stores?.map(s => s.name).join(', ')}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleBulkMerge}
                disabled={!mainOrderId || actionLoading}
                className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
              >
                {actionLoading ? 'Merging...' : 'Merge Orders'}
              </button>
              <button
                onClick={() => { setShowMergeModal(false); setMainOrderId(null); }}
                className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TBA Submission Modal */}
      {showTbaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">Submit {selectedIds.length} Order(s) for TBA Assessment</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              Select a TBA approver to notify. The order(s) will be sent to the supplier for assessment.
            </p>
            <select
              value={tbaApprover || ''}
              onChange={(e) => setTbaApprover(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            >
              <option value="">Select TBA Approver...</option>
              {(filtersData?.approvers || []).map((a: any) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleTbaSubmit}
                disabled={!tbaApprover || actionLoading}
                className="flex-1 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Submit for TBA'}
              </button>
              <button
                onClick={() => { setShowTbaModal(false); setTbaApprover(null); }}
                className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receive Stock Modal */}
      {showReceiveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">Receive Stock</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              Confirm the date stock was received for order #{selectedIds[0]}.
            </p>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Received Date</label>
            <input
              type="date"
              value={receiveDate}
              onChange={(e) => setReceiveDate(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleReceiveStock}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Confirm Receive'}
              </button>
              <button
                onClick={() => setShowReceiveModal(false)}
                className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Metrofile Confirmation Modal */}
      {showMetrofileModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">Send to Metrofile</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              This will send documents for {selectedIds.length} order(s) to Metrofile. Continue?
            </p>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSendToMetrofile}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Yes, Send'}
              </button>
              <button
                onClick={() => setShowMetrofileModal(false)}
                className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Documents Modal */}
      {selectedPoForDocuments && (
        <ViewDocumentsModal
          po={selectedPoForDocuments}
          isOpen={isDocumentsModalOpen}
          onDeleteDocument={handleDeleteDocument}
          onClose={() => {
            setIsDocumentsModalOpen(false);
            setSelectedPoForDocuments(null);
          }}
        />
      )}

      {/* Add Document Modal */}
      {selectedPoForAddDocument && (
        <AddDocumentModal
          po={selectedPoForAddDocument}
          isOpen={isAddDocumentModalOpen}
          onClose={() => {
            setIsAddDocumentModalOpen(false);
            setSelectedPoForAddDocument(null);
          }}
          onDocumentUploaded={handleDocumentUploaded}
        />
      )}

      {/* SAGE Export Modal */}
      {showSageExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">AP Export</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Export purchase orders to a CSV file for AP import. Leave blank to export all approved orders.
              {selectedIds.length > 0 && ` ${selectedIds.length} order(s) selected will be exported.`}
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Date From</label>
                <input
                  type="date"
                  value={sageExportParams.date_from}
                  onChange={(e) => setSageExportParams({ ...sageExportParams, date_from: e.target.value })}
                  className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Date To</label>
                <input
                  type="date"
                  value={sageExportParams.date_to}
                  onChange={(e) => setSageExportParams({ ...sageExportParams, date_to: e.target.value })}
                  className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Order Numbers (comma-separated)</label>
                <input
                  type="text"
                  value={sageExportParams.order_numbers}
                  onChange={(e) => setSageExportParams({ ...sageExportParams, order_numbers: e.target.value })}
                  placeholder="e.g. PO-001, PO-002"
                  className="w-full mt-1 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSageExport}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                {actionLoading ? 'Exporting...' : 'Export CSV'}
              </button>
              <button
                onClick={() => setShowSageExportModal(false)}
                className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Items Modal */}
      {showManageItemsModal && manageItemsPoId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-5xl max-h-[90vh] overflow-y-auto p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Manage Items — Order #{manageItemsPoData?.order_number || manageItemsPoData?.reference_number || manageItemsPoId}
              </h3>
              <button onClick={() => { setShowManageItemsModal(false); setManageItemsPoId(null); setManageItemsList([]); setManageItemsPoData(null); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {manageItemsLoading ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">Loading items...</div>
            ) : (
              <>
                {/* Excel Import Section */}
                <div className="mb-4 p-3 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-green-800 dark:text-green-300">Excel Import — Order Line Items</h4>
                    <button
                      onClick={handleDownloadTemplate}
                      className="text-xs text-green-700 dark:text-green-400 hover:underline"
                    >
                      Download Template
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
                      className="text-xs flex-1"
                    />
                    <button
                      onClick={handleExcelImport}
                      disabled={!excelFile || excelImportLoading}
                      className="px-3 py-1.5 bg-green-500 text-white rounded text-xs hover:bg-green-600 disabled:opacity-50"
                    >
                      {excelImportLoading ? 'Importing...' : 'Import Excel'}
                    </button>
                  </div>
                </div>

                {/* Load ATR Items Section */}
                {manageItemsPoData?.requisition?.id && (
                  <div className="mb-4 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        onClick={handleLoadAtrItems}
                        disabled={atrLoading}
                        className="px-3 py-1.5 bg-amber-500 text-white rounded text-xs hover:bg-amber-600 disabled:opacity-50 flex items-center gap-1.5"
                      >
                        {atrLoading ? (
                          <>
                            <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                            Loading ATR Items...
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                            Load ATR Items
                          </>
                        )}
                      </button>
                      <label className="flex items-center gap-1.5 text-xs text-amber-800 dark:text-amber-300">
                        <input
                          type="checkbox"
                          checked={keepExistingItems}
                          onChange={(e) => setKeepExistingItems(e.target.checked)}
                          className="rounded"
                        />
                        Keep Existing Items
                      </label>
                      {!keepExistingItems && (
                        <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                          This will replace all existing items!
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Items Table */}
                <div className="overflow-x-auto mb-4 rounded-lg border border-slate-200 dark:border-slate-700">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                      <tr className="text-left text-xs font-semibold text-slate-500 dark:text-slate-400">
                        <th className="px-3 py-2">Item Code</th>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Description</th>
                        <th className="px-3 py-2">Qty</th>
                        <th className="px-3 py-2">Price</th>
                        <th className="px-3 py-2">VAT</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                      {manageItemsList.length === 0 && (
                        <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No items found.</td></tr>
                      )}
                      {manageItemsList.map((item) => (
                        <tr key={item.id}>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              defaultValue={item.item_code || ''}
                              onBlur={(e) => { if (e.target.value !== (item.item_code || '')) handleUpdateItem(item.id, 'item_code', e.target.value); }}
                              className="w-24 px-2 py-1 rounded border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              defaultValue={item.name || ''}
                              onBlur={(e) => { if (e.target.value !== (item.name || '')) handleUpdateItem(item.id, 'name', e.target.value); }}
                              className="w-32 px-2 py-1 rounded border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              defaultValue={item.description || ''}
                              onBlur={(e) => { if (e.target.value !== (item.description || '')) handleUpdateItem(item.id, 'description', e.target.value); }}
                              className="w-40 px-2 py-1 rounded border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              defaultValue={item.quantity ?? ''}
                              onBlur={(e) => { if (e.target.value !== String(item.quantity ?? '')) handleUpdateItem(item.id, 'quantity', e.target.value ? Number(e.target.value) : undefined); }}
                              className="w-16 px-2 py-1 rounded border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              defaultValue={item.price ?? ''}
                              onBlur={(e) => { if (e.target.value !== String(item.price ?? '')) handleUpdateItem(item.id, 'price', e.target.value ? Number(e.target.value) : undefined); }}
                              className="w-20 px-2 py-1 rounded border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              defaultValue={item.vat || 'inclusive'}
                              onChange={(e) => handleUpdateItem(item.id, 'vat', e.target.value)}
                              className="px-2 py-1 rounded border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-xs"
                            >
                              <option value="inclusive">Incl</option>
                              <option value="exclusive">Excl</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              className="text-red-500 hover:text-red-700 text-xs"
                              title="Remove item"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals Section */}
                <div className="mb-4 flex justify-end">
                  <div className="w-64 space-y-1 text-sm">
                    <div className="flex justify-between text-slate-600 dark:text-slate-300">
                      <span>Sub-total:</span>
                      <span>{formatCurrency(itemsSubtotal)}</span>
                    </div>
                    <div className="flex justify-between text-slate-600 dark:text-slate-300">
                      <span>VAT (15%):</span>
                      <span>{formatCurrency(itemsVatAmount)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-slate-900 dark:text-white">
                      <span>Total:</span>
                      <span>{formatCurrency(itemsTotal)}</span>
                    </div>
                    <div className="border-t border-slate-200 dark:border-slate-700 my-1" />
                    <div className="flex justify-between text-slate-600 dark:text-slate-300">
                      <span>Quote Amount:</span>
                      <span className="font-medium">{quoteAmount > 0 ? formatCurrency(quoteAmount) : '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Add New Item */}
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Add New Item</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <input
                      type="text"
                      placeholder="Item Code"
                      value={newItem.item_code}
                      onChange={(e) => setNewItem({ ...newItem, item_code: e.target.value })}
                      className="px-2 py-1.5 rounded border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-xs"
                    />
                    <input
                      type="text"
                      placeholder="Name *"
                      value={newItem.name}
                      onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                      className="px-2 py-1.5 rounded border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-xs"
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={newItem.description}
                      onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      className="px-2 py-1.5 rounded border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-xs"
                    />
                    <input
                      type="number"
                      placeholder="Qty"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                      className="px-2 py-1.5 rounded border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-xs"
                    />
                    <input
                      type="number"
                      placeholder="Price"
                      value={newItem.price}
                      onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                      className="px-2 py-1.5 rounded border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-xs"
                    />
                    <select
                      value={newItem.vat}
                      onChange={(e) => setNewItem({ ...newItem, vat: e.target.value })}
                      className="px-2 py-1.5 rounded border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-xs"
                    >
                      <option value="inclusive">VAT Inclusive</option>
                      <option value="exclusive">VAT Exclusive</option>
                    </select>
                    <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
                      <input type="checkbox" checked={newItem.non_vat} onChange={(e) => setNewItem({ ...newItem, non_vat: e.target.checked })} />
                      Non-VAT
                    </label>
                    <button
                      onClick={handleAddItem}
                      disabled={!newItem.name}
                      className="px-3 py-1.5 bg-green-500 text-white rounded text-xs hover:bg-green-600 disabled:opacity-50"
                    >
                      Add Item
                    </button>
                  </div>
                </div>

                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => { setShowManageItemsModal(false); setManageItemsPoId(null); setManageItemsList([]); setManageItemsPoData(null); }}
                    className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderListPage;
